import { CHAT_SESSION_CHANNEL } from "./constants";
import { callIntercom } from "./intercom-script";

const ENDED = "ended";

const listeners = new Set<() => void>();

/** Hear about this tab's chat session ending. Returns the unsubscribe. */
export function onChatSessionEnded(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Expire Intercom's session cookie for this host and each parent domain.
 *  Intercom writes it on the widest domain it can, where help.cognota.com and
 *  Classic read it too, so a signed-out person's conversations would otherwise
 *  stay on this browser for a week. Only the session cookie goes, which is what
 *  Intercom's own shutdown clears. */
export function clearIntercomSessionCookies(): void {
  const names = document.cookie
    .split(";")
    .map((pair) => pair.trim().split("=")[0])
    .filter((name) => name.startsWith("intercom-session-"));
  if (names.length === 0) return;
  const expired = "expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  for (const name of names) {
    document.cookie = `${name}=; ${expired}`;
    for (const domain of parentDomains(window.location.hostname)) {
      document.cookie = `${name}=; ${expired}; domain=${domain}`;
    }
  }
}

/** `my.cognota.com` → `my.cognota.com`, `cognota.com`. A single label has none. */
function parentDomains(hostname: string): string[] {
  const labels = hostname.split(".");
  const domains: string[] = [];
  for (let i = 0; i < labels.length - 1; i++) domains.push(labels.slice(i).join("."));
  return domains;
}

/** Shut the messenger down and clear its session cookie, so a blocked or
 *  half-loaded script still leaves nothing behind. */
export function shutDownIntercom(): void {
  callIntercom("shutdown");
  clearIntercomSessionCookies();
}

/**
 * End the Intercom session in this browser: `shutDownIntercom`, then tell this
 * tab's listeners and, unless `broadcast` is false, every other open NextGen tab.
 *
 * Invariant (NG-3452, AC8): every way a NextGen session ends reaches this.
 * `AuthService.signOut()` calls it first (an SSO sign-out may land on the
 * identity provider, never on our sign-in page); `UserStore.fetchCurrentUser`
 * calls it whenever it finds nobody signed in, which catches every other ending
 * on the next page load; and the API client's expired-session branch calls it
 * before leaving for sign-in. A new sign-out path that bypasses all three must
 * call it too.
 */
export function endChatSession({ broadcast = true }: { broadcast?: boolean } = {}): void {
  shutDownIntercom();
  for (const listener of listeners) {
    try {
      listener();
    } catch (error) {
      console.warn("[intercom] a chat-ended listener threw", error);
    }
  }
  if (broadcast) tellOtherTabs();
}

function tellOtherTabs(): void {
  if (typeof BroadcastChannel === "undefined") {
    console.warn("[intercom] no BroadcastChannel: other open tabs keep their chat until reload");
    return;
  }
  try {
    const channel = new BroadcastChannel(CHAT_SESSION_CHANNEL);
    channel.postMessage(ENDED);
    channel.close();
  } catch (error) {
    console.warn("[intercom] could not tell other tabs that chat ended", error);
  }
}

/** End chat in this tab when another tab ends it. Returns the disposer. */
export function listenForChatSessionEndedElsewhere(): () => void {
  if (typeof BroadcastChannel === "undefined") return () => {};
  const channel = new BroadcastChannel(CHAT_SESSION_CHANNEL);
  channel.onmessage = (event: MessageEvent) => {
    if (event.data === ENDED) endChatSession({ broadcast: false });
  };
  return () => channel.close();
}
